import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { GrowthCreditEntry } from '@feathr/blackbox';
import { EGrowthCreditEntryClass } from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';
import { Button, CardV2 as Card, DatePicker, Select, Table } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { getIconForAction, TimeFormat } from '@feathr/hooks';
import type { IObject } from '@feathr/rachis';

import { creditsRedeemedColumns } from './creditsRedeemedColumns';
import ExportRedemptionsModal from './ExportRedemptionsModal';

import * as styles from './CreditsRedeemedTable.css';

interface ITypeOption extends ISelectOption {
  id: string;
  name: string;
}

const REDEMPTION_CLASSES = [
  EGrowthCreditEntryClass.MediaRedemption,
  EGrowthCreditEntryClass.InvoiceRedemption,
];

function CreditsRedeemedTable(): JSX.Element {
  const { t } = useTranslation();
  const { GrowthCreditEntries } = useStore();
  const [typeFilter, setTypeFilter] = useState<ITypeOption | undefined>(undefined);
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);

  const handleOpenExportModal = useCallback((): void => {
    setIsExportModalOpen(true);
  }, []);

  const handleCloseExportModal = useCallback((): void => {
    setIsExportModalOpen(false);
  }, []);

  const typeOptions: ITypeOption[] = useMemo(
    () => [
      { id: 'all', name: t('All types') },
      { id: EGrowthCreditEntryClass.MediaRedemption, name: t('Media Balance') },
      { id: EGrowthCreditEntryClass.InvoiceRedemption, name: t('Invoice Payment') },
    ],
    [t],
  );

  const filters: IObject = useMemo(() => {
    const result: IObject = {};

    if (typeFilter && typeFilter.id !== 'all') {
      result._cls = typeFilter.id;
    } else {
      result._cls__in = REDEMPTION_CLASSES;
    }

    if (startDate) {
      result.date_entered__gte = startDate;
    }

    if (endDate) {
      result.date_entered__lte = endDate;
    }

    return result;
  }, [typeFilter, startDate, endDate]);

  function handleTypeChange(option: ITypeOption | null): void {
    setTypeFilter(option ?? undefined);
  }

  function handleStartDateChange(date?: string): void {
    setStartDate(date);
  }

  function handleEndDateChange(date?: string): void {
    setEndDate(date);
  }

  const columns = useMemo(() => creditsRedeemedColumns({ t }), [t]);

  const filterBar = (
    <div className={styles.filterBar}>
      <div className={styles.filters}>
        <Select<ITypeOption>
          className={styles.typeSelect}
          name={'redemption-type-filter'}
          onSelectSingle={handleTypeChange}
          options={typeOptions}
          placeholder={t('All types')}
          value={typeFilter ?? typeOptions[0]}
        />
        <DatePicker
          format={TimeFormat.isoDate}
          isClearable={true}
          onDateStrChange={handleStartDateChange}
          placeholder={t('Start date')}
          showCalendarIcon={true}
          value={startDate}
        />
        <DatePicker
          format={TimeFormat.isoDate}
          isClearable={true}
          onDateStrChange={handleEndDateChange}
          placeholder={t('End date')}
          showCalendarIcon={true}
          value={endDate}
        />
      </div>
    </div>
  );

  return (
    <>
      <Card width={'full'}>
        <Card.Header
          description={t('Track your credit redemptions and transactions')}
          title={t('Redemption History')}
        >
          <Button
            onClick={handleOpenExportModal}
            prefix={getIconForAction('export')}
            type={'secondary'}
          >
            {t('Export as CSV')}
          </Button>
        </Card.Header>
        <Card.Content>
          {filterBar}
          <Table<GrowthCreditEntry>
            collection={GrowthCreditEntries}
            columns={columns}
            filters={filters}
            initialPagesize={10}
            initialSort={[{ id: 'date_entered', desc: true }]}
          />
        </Card.Content>
      </Card>
      <ExportRedemptionsModal onClose={handleCloseExportModal} opened={isExportModalOpen} />
    </>
  );
}

export default observer(CreditsRedeemedTable);
